import React, { useEffect, useState, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { Fade } from "react-reveal"
import { useForm } from "react-hook-form"
import toast from "react-hot-toast"
import { UserIcon, MapPinIcon, HashtagIcon, PencilIcon } from '@heroicons/react/24/solid'

import { api } from '../../api'
import { StandardDialog } from "./"
import { FormCardInput } from '../../components/FormCard'
import FormBtn from "../elements/Buttons/FormBtn"
import { BRANCH_OPTIONS, COMPANY_SIZE_OPTIONS, REMOTE_PRODUCT_SELECTED_KEY, SECTOR_OPTIONS, VALIDATION_SIREN } from "../../constants"
import { selectUserInfo, updateCompany } from "../../features/users/userInfoSlice"
import ToastAction from "../ToastAction"
import AutoCompleteAddress from "../AutoCompleteAddress"

/**
 * 
 * @param {{ data: Company, open: boolean }} props 
 * @returns {JSX.Element}
 */
export function ClientExtraInfoModal({ data, open }) {
	const history = useHistory()
	const dispatch = useDispatch();
	const [showItems, setShowItems] = useState(false)
	const {
		register,
		handleSubmit,
		watch,
		setValue,
		setError,
		formState: { errors }
	} = useForm({
		defaultValues: {
			legal_name: data?.legal_name,
			address: data?.address,
			siren: data?.siren,
			tva: data?.tva,
			size: data?.size,
			sector: data?.sector,
			branch: data?.branch
		}
	});
	const userInfoContext = useSelector(selectUserInfo)
	const subscribe = useSelector(state => state.subscriptions.product)
	const [show, setShow] = useState(open)
	const [submitting, setSubmitting] = useState(false)

	const sectorSelected = watch('sector')

	const address = watch('address')

	const _showItems = useCallback(() => {
		setShowItems(true)
	}, [])

	const _hideItems = useCallback(() => {
		setTimeout(() => {
			setShowItems(false)
		}, 500)
	}, [])

	useEffect(() => {
		if (userInfoContext?.user?.admin) setShow(open)
	}, [open])

	const closeModal = useCallback(() => {
		setShow(false)
	}, [])

	const save = useCallback(async company => {
		if (!company.sector) return setError('sector', { type: "required" }, true)
		if (!company.branch) return setError('branch', { type: "required" }, true)
		if (!company.size) return setError('size', { type: "required" }, true)

		setSubmitting(true)
		try {
			const { company_id } = userInfoContext?.user
			const company_obj = {
				...company,
				sector: company.other_sector || company.sector,
			}
			company_obj.company_id = company_id
			await api.company().update(company_obj)
			dispatch(updateCompany({ ...userInfoContext?.user, company: company_obj }))
			setSubmitting(false)

			closeModal()
			if (localStorage.getItem(REMOTE_PRODUCT_SELECTED_KEY) || !subscribe) history.push('/account/forfait')
		} catch (error) {
			setSubmitting(false)
			toast.custom((t) => <ToastAction saraExpression="sorry" toastRef={t} label="Oups !!" description="Nous avons rencontré un problème. Veuillez réessayer SVP !" />)
		}
	}, [userInfoContext?.user?.company_id, subscribe])

	return (
		<StandardDialog open={show} onClose={closeModal}>
			<form onSubmit={handleSubmit(save)} className="space-y-2">
				<h2 className="font-medium text-lg text-gray-600 text-center">Informations supplémentaires de votre compte</h2>
				<FormCardInput
					register={register}
					label="Nom légal*"
					name="legal_name"
					errorType={errors?.legal_name?.type}
					icon={<UserIcon className="w-4 h-4 text-gray-500" />}
				/>
				<div className='w-full relative'>
					<FormCardInput
						onFocus={_showItems}
						onBlur={_hideItems}
						register={register}
						label="Adresse du siege*"
						name="address"
						errorType={errors?.address?.type}
						icon={<MapPinIcon className="w-4 h-4 text-gray-500" />}
					/>
					<AutoCompleteAddress name='address' setValue={setValue} query={address} showItems={showItems} />
				</div>
				<FormCardInput
					register={register}
					label="SIREN*"
					name="siren"
					rules={{ required: true, pattern: VALIDATION_SIREN }}
					errorType={errors?.siren?.type}
					icon={<HashtagIcon className="w-4 h-4 text-gray-500" />}
				/>
				<FormCardInput
					register={register}
					rules={{ required: false }}
					label="TVA Intracommunautaire"
					name="tva"
					errorType={errors?.tva?.type}
					icon={<HashtagIcon className="w-4 h-4 text-gray-500" />}
				/>
				<FormCardInput
					type='select'
					options={COMPANY_SIZE_OPTIONS}
					register={register}
					label="Taille de l’entreprise*"
					name="size"
					errorType={errors?.size?.type}
				/>
				<FormCardInput
					type="select"
					options={SECTOR_OPTIONS}
					register={register}
					label="Secteur d’activité*"
					name="sector"
					errorType={errors?.sector?.type}
				/>
				{
					sectorSelected === SECTOR_OPTIONS.at(-1) && <Fade top>
						<FormCardInput
							register={register}
							placeholder="Entrez votre secteur d'activité"
							name="other_sector"
							icon={<PencilIcon className="w-4 h-4 text-gray-500" />}
						/>
					</Fade>
				}
				<FormCardInput
					placeholder={BRANCH_OPTIONS + '...'}
					register={register}
					label="Branche d’activité*"
					name="branch"
					errorType={errors?.branch?.type}
				/>
				<FormBtn disabled={submitting} />
				<div className="cursor-pointer text-sm text-gray-600 text-center underline" onClick={closeModal}>
					Quitter et commencer plus tard
				</div>
			</form>
		</StandardDialog>
	)
}

